import { useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { kybChecksEnum, shareholderChecksEnum } from '../kyb.enums'
import { Box, Checkbox, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { documentByTypeAndCountry, DocumentValidation } from '../constants'
import { useCreateKybMutation } from '../../../services/guenoApi'
import { countryDictionary } from '../../../components/common/flag/countries'
import toaster from '../../../toaster'
import { KybUtils } from '../kyb.utils'

const KybNoRowsOverlay = ({ isKyb = true, defaultDocumentNumber = '' }) => {
  const navigate = useNavigate()
  const { t } = useTranslation(['common','messages'	])
  const [createKyb, { isLoading }] = useCreateKybMutation({ fixedCacheKey: 'create-kyb' })
  const { currentCountry, countrySegment } = useSelector((state) => state.kybCountry)
  const { emailClient } = useSelector((state) => state.login)
  const [documentNumber, setDocumentNumber] = useState(defaultDocumentNumber)
  const [isValidDocument, setIsValidDocument] = useState(false)
  const [nationality, setNationality] = useState(currentCountry)
  const [runChecks, setRunChecks] = useState(true)

  // Filter checks based on email restrictions and default visibility
  const allKybChecks = useMemo(() => {
    const checks = kybChecksEnum[currentCountry] || []
    return checks.filter((checkKey) => {
      // Filter hidden checks (priority check)
      if (KybUtils.isCheckHidden(checkKey)) {
        return false
      }
      // Filter by email restrictions
      if (KybUtils.isAmlddCheckKey(checkKey) && !KybUtils.canViewCheck(checkKey, emailClient, 'amldd')) {
        return false
      }
      if (KybUtils.isJapJusCheckKey(checkKey) && !KybUtils.canViewCheck(checkKey, emailClient, 'jap_jus')) {
        return false
      }
      // Filter checks that should be disabled by default
      if (KybUtils.isCheckDisabledByDefault(checkKey)) {
        return false
      }
      return true
    })
  }, [currentCountry, emailClient])

  const allShareholderChecks = useMemo(() => {
    const checks = shareholderChecksEnum[currentCountry] || []
    return checks.filter((checkKey) => {
      // Filter hidden checks (priority check)
      if (KybUtils.isCheckHidden(checkKey)) {
        return false
      }
      // Filter by email restrictions
      if (KybUtils.isAmlddCheckKey(checkKey) && !KybUtils.canViewCheck(checkKey, emailClient, 'amldd')) {
        return false
      }
      if (KybUtils.isJapJusCheckKey(checkKey) && !KybUtils.canViewCheck(checkKey, emailClient, 'jap_jus')) {
        return false
      }
      // Filter checks that should be disabled by default
      if (KybUtils.isCheckDisabledByDefault(checkKey)) {
        return false
      }
      return true
    })
  }, [currentCountry, emailClient])

  const [kybChecks, setKybChecks] = useState(allKybChecks)
  const [shareholderChecks, setShareholderChecks] = useState(allShareholderChecks)

  const entityType = isKyb ? 'kyb' : 'kyc'
  const documentTypes = documentByTypeAndCountry[entityType][currentCountry] || []
  const documentTypesText = `(${documentTypes?.join(', ')})`

  // Format check names for display (replace underscores with spaces)
  const formatCheckName = (checkKey) => {
    // Try to get translation without namespace
    const translation = t(`common:${checkKey}`, { defaultValue: '' })

    // If translation exists and is not empty, use it
    if (translation && translation.trim() !== '') {
      return translation
    }

    // Otherwise format the key: replace underscores with spaces (keep uppercase)
    return checkKey.split('_').join(' ')
  }

  const buildBody = () => {
    const body = { documentNumber }

    if (runChecks) {
      body.runChecks = runChecks
      body.shareholderChecks = shareholderChecks
    }

    if (runChecks && isKyb) {
      body.kybChecks = kybChecks
    }

    if (!isKyb) {
      body.nationality = nationality
    }

    return body
  }

  const handleSubmit = async () => {
    try {
      const { data, error } = await createKyb({
        countrySegment,
        isKyb,
        body: buildBody(),
      })
      if (data?.success && data?.data) {
        navigate(`/${isKyb ? 'kyb' : 'kyc'}/${currentCountry}/detail/${data.data._id}`)
      } else if (error?.data) {
        throw new Error(error?.data?.message || t('messages:unexpectedErrorValues'))
      } else {
        throw new Error(data?.responseBody?.message || data?.info || 'Unexpected')
      }
    } catch (err) {
      toaster.error(err.message ?  t('messages:'+err.message) : t('messages:unexpectedErrorValues'))
    }
  }

  useEffect(() => {
    if (documentNumber) {
      const regex = DocumentValidation[entityType][currentCountry]
      setIsValidDocument(documentNumber.match(regex))
    }
  }, [currentCountry, documentNumber, entityType])

  // Update check selections when filtered lists change
  useEffect(() => {
    setKybChecks(allKybChecks)
  }, [allKybChecks])

  useEffect(() => {
    setShareholderChecks(allShareholderChecks)
  }, [allShareholderChecks])

  return (
    <Box className="custom-overlay" sx={{ paddingBottom: '22rem', marginTop: '8rem' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
        <Typography sx={{ fontSize: '1rem', fontWeight: '500', color: '#66766B' }}>
          {t('messages:kyb.nothingToShow')}
        </Typography>

        <Box
          sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'unset', gap: '.5rem' }}
        >
          <TextField
            size="medium"
            value={documentNumber}
            error={!isValidDocument}
            fullWidth
            className="forceWidth"
            onChange={(event) => setDocumentNumber(event.target.value?.replace(/[^0-9]/g, ''))}
            disabled={isLoading}
            style={{ width: 350 }}
            helperText={!isValidDocument ? t('messages:kyb.invalidCuit') : ''}
            InputProps={{
              placeholder: t('common:kyb.documentNumberInput', { documentTypes: documentTypesText }),
            }}
          />

          {!isKyb && (
            <Box>
              <Select
                size="small"
                className="forceWidth"
                value={nationality}
                onChange={(e) => setNationality(e.target.value)}
                placeholder={`${t('common:select')} ${t('common:nationality')}`}
                displayEmpty
                fullWidth
                disabled={isLoading}
                renderValue={(value) =>
                  value?.length ? countryDictionary[value] : `${t('common:select')} ${t('common:nationality')}`
                }
              >
                {Object.keys(countryDictionary)?.map((key) => {
                  return (
                    <MenuItem key={key} value={key}>
                      {countryDictionary[key]}
                    </MenuItem>
                  )
                })}
              </Select>
            </Box>
          )}
          {(!!kybChecks?.length || !!shareholderChecks?.length) && (
            <Box>
              <Tooltip placement="top" title={t('common:runChecks')}>
                <Checkbox disabled={isLoading} checked={runChecks} onChange={() => setRunChecks(!runChecks)} />
              </Tooltip>
            </Box>
          )}

          <LoadingButton
            size="small"
            variant="contained"
            type="submit"
            disabled={!documentNumber || !isValidDocument}
            loading={isLoading}
            onClick={handleSubmit}
          >
            <span>{t('common:create')}</span>
          </LoadingButton>
        </Box>
        {isKyb && runChecks && !!kybChecks?.length && (
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '.5rem' }}>
            <Box sx={{ width: 160, textAlign: 'right' }}>
              <Typography variant="body1">{t('common:kybChecks')}:</Typography>
            </Box>
            <Select
              className="forceWidth"
              size="small"
              multiple
              fullWidth
              value={kybChecks}
              onChange={(e) =>
                setKybChecks(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)
              }
              renderValue={(selected) => selected.map((name) => formatCheckName(name)).join(', ')}
              disabled={isLoading}
            >
              {allKybChecks?.map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox checked={kybChecks.indexOf(name) > -1} />
                  <Typography>{formatCheckName(name)}</Typography>
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
        {runChecks && !!shareholderChecks?.length && (
          (currentCountry !== 'AR' || (currentCountry === 'AR' && !isKyb)) && (
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '.5rem' }}>
              <Box sx={{ width: 160, textAlign: 'right' }}>
                <Typography variant="body1">{t('common:shareholdersChecks')}:</Typography>
              </Box>
              <Select
                className="forceWidth"
                size="small"
                multiple
                fullWidth
                value={shareholderChecks}
                onChange={(e) =>
                  setShareholderChecks(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)
                }
                renderValue={(selected) => selected.map((name) => formatCheckName(name)).join(', ')}
                disabled={isLoading}
              >
                {allShareholderChecks.map((name) => (
                  <MenuItem key={name} value={name}>
                    <Checkbox checked={shareholderChecks.indexOf(name) > -1} />
                    <Typography>{formatCheckName(name)}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )
        )}
      </Box>
    </Box>
  )
}

export default KybNoRowsOverlay
